import React from 'react';
import { useField } from 'react-final-form';
import { useIntl } from 'react-intl';

import StarsAutocomplete from '@swf/components/Inputs/StarsAutocomplete';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import Text from '@swf/components/Inputs/Text';
import Translation from '@swf/components/Translation/Translation';
import { dialingCodeSelectOptions } from '@swf/core/utils/phoneVerification';
import {
  maxLength,
  minLength,
  mustContainOnlyLatinCharacters,
  mustNotContainSymbols,
  numbersOrSpace,
  required
} from '@swf/core/utils/validators';
import occupations from '@swf/generated/occupations.json';

import { PEPHIOOptions } from '../PEPHIOSelection/PEPHIOSelection';

import {
  TXTCLI_Country_code,
  TXTCLI_Optional,
  TXTWCL_CompanyXs_address,
  TXTWCL_CompanyXs_phone,
  TXTWCL_Company_name,
  TXTWCL_Must_be_shorter_than_N0_charac_ELL,
  TXTWCL_Occupation,
  TXTWCL_Organization_Institution_name,
  TXTWCL_Please_select_your_occupation__ELL,
  TXTWCL_Position_Title,
  TXTWCL_Source_of_funds_being_deposite_ELL,
  TXTWCL_Source_of_wealth_used_for_fund_ELL,
  TXTWCL_This_field_must_be_less_than_N_ELL,
  TXTWCL_This_information_is_kept_confi_ELL
} from './OccupationDetails.messages.json';
import styles from './OccupationDetails.scss';
import OccupationInfo from './OccupationInfo/OccupationInfo';

export const RESTRICTED_SYMBOLS = /[[~`!@#$%^&*()_+={}|:;"<,>.?/\]]/;
export const RESTRICTED_SYMBOLS_COMPANY = /[[~`!@#$%^&*()_+={}|:;"<,>?/\]]/;
export const RESTRICTED_SYMBOLS_ADDRESS = /[[~`!@$%^*_={}|:;"<>?\]]/;

const OccupationDetails: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    input: { value: pepHioSelection }
  } = useField('pepHioSelection');

  return (
    <>
      <Translation {...TXTWCL_This_information_is_kept_confi_ELL} />
      <br />
      <OccupationInfo />
      <StarsAutocomplete
        hideLabel
        dataTestId="autocomplete-occupation"
        id="occupation"
        options={occupations}
        matchFrom="start"
        limit={100}
        label={
          pepHioSelection === PEPHIOOptions.PEPOrHIO ? TXTWCL_Position_Title : TXTWCL_Occupation
        }
        maxLength={50}
        validators={[required(TXTWCL_Please_select_your_occupation__ELL)]}
      />
      {[PEPHIOOptions.PEPOrHIORelated, PEPHIOOptions.NotPEPOrHIO].includes(pepHioSelection) && (
        <Text
          id="companyName"
          label={TXTWCL_Company_name}
          placeholder={formatMessage(TXTCLI_Optional)}
          validators={[
            maxLength(
              34,
              { ...TXTWCL_Must_be_shorter_than_N0_charac_ELL, values: { 0: 50 } },
              true
            ),
            mustNotContainSymbols(RESTRICTED_SYMBOLS_COMPANY),
            mustContainOnlyLatinCharacters()
          ]}
        />
      )}
      <StarsDropDown
        id="dialingCode"
        label={TXTCLI_Country_code}
        options={dialingCodeSelectOptions}
      />
      <Text
        id="companyPhone"
        label={TXTWCL_CompanyXs_phone}
        numericKeyboard
        placeholder={formatMessage(TXTCLI_Optional)}
        className={styles.phoneInput}
        validators={[
          numbersOrSpace(undefined, true),
          maxLength(10, { ...TXTWCL_This_field_must_be_less_than_N_ELL, values: { 0: 11 } }, true)
        ]}
      />
      <Text
        id="companyAddress"
        label={TXTWCL_CompanyXs_address}
        placeholder={formatMessage(TXTCLI_Optional)}
        validators={[
          maxLength(49, { ...TXTWCL_Must_be_shorter_than_N0_charac_ELL, values: { 0: 50 } }, true),
          mustNotContainSymbols(RESTRICTED_SYMBOLS_ADDRESS),
          mustContainOnlyLatinCharacters()
        ]}
      />
      {pepHioSelection === PEPHIOOptions.PEPOrHIO && (
        <>
          <Text
            id="companyName"
            label={TXTWCL_Organization_Institution_name}
            validators={[
              required(),
              minLength(2),
              maxLength(49),
              mustNotContainSymbols(RESTRICTED_SYMBOLS_COMPANY),
              mustContainOnlyLatinCharacters()
            ]}
          />
          <Text
            id="sourceOfFunds"
            label={TXTWCL_Source_of_funds_being_deposite_ELL}
            validators={[
              required(),
              minLength(2),
              maxLength(49),
              mustNotContainSymbols(RESTRICTED_SYMBOLS),
              mustContainOnlyLatinCharacters()
            ]}
          />
          <Text
            id="sourceOfWealth"
            label={TXTWCL_Source_of_wealth_used_for_fund_ELL}
            validators={[
              required(),
              minLength(2),
              maxLength(49),
              mustNotContainSymbols(RESTRICTED_SYMBOLS),
              mustContainOnlyLatinCharacters()
            ]}
          />
        </>
      )}
    </>
  );
};

export default OccupationDetails;
